import React, { useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Badge  from "@react/shared/ui/Badge"
import DownPaymentModal from "./DownPaymentModal"
import { fmt, dateFmt } from "../helpers/format"
import ActionButton from "./ActionButton"

const STATUS_TONE = { "In progress": "warning", "Confirmed": "blue", "Paid": "success" }

const ExistingBlock = ({ downPayment }) => {
  const invoice = downPayment.invoice
  return (
    <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex flex-wrap items-center gap-4">
      <div className="flex items-center gap-2 shrink-0">
        <span className="w-8 h-8 grid place-items-center rounded-full bg-amber-50 text-amber-700">
          <FontAwesomeIcon icon="hand-holding-dollar" />
        </span>
        <div className="flex flex-col">
          <span className="text-[10.5px] uppercase tracking-wider font-medium text-slate-500">
            {I18n.t("projects.down_payments.banner.existing_eyebrow")}
          </span>
          <a href={invoice.path} className="text-[13px] font-semibold text-slate-900 hover:text-blue-700">
            {invoice.number || invoice.title}
          </a>
        </div>
      </div>

      <div className="hidden md:block w-px h-8 bg-slate-200" />

      <div className="flex flex-col">
        <span className="text-[10.5px] uppercase tracking-wider font-medium text-slate-500">
          {I18n.t("projects.down_payments.banner.subtotal")}
        </span>
        <span className="text-[14px] font-semibold text-slate-900 tabular-nums">{fmt(invoice.subtotal)}</span>
      </div>

      <div className="flex flex-col">
        <span className="text-[10.5px] uppercase tracking-wider font-medium text-slate-500">
          {I18n.t("projects.down_payments.banner.deducted")}
        </span>
        <span className="text-[14px] tabular-nums text-slate-700">{fmt(downPayment.deducted)}</span>
      </div>

      <div className="flex flex-col">
        <span className="text-[10.5px] uppercase tracking-wider font-medium text-slate-500">
          {I18n.t("projects.down_payments.banner.remaining")}
        </span>
        <span className="text-[14px] tabular-nums text-slate-900 font-medium">{fmt(downPayment.remaining)}</span>
      </div>

      <div className="ml-auto flex items-center gap-3">
        {invoice.status && (
          <Badge tone={STATUS_TONE[invoice.status] || "neutral"} variant="soft" size="sm">
            {invoice.status_label}
          </Badge>
        )}
        {invoice.creation_date && (
          <span className="text-[12px] text-slate-500">{dateFmt(invoice.creation_date)}</span>
        )}
      </div>
    </div>
  )
}

const EmptyBlock = ({ downPayment, canCreate, onOpen }) => (
  <div className="bg-slate-50 border border-dashed border-slate-300 rounded-xl px-4 py-3 flex flex-wrap items-center gap-4">
    <div className="flex items-center gap-2">
      <span className="w-8 h-8 grid place-items-center rounded-full bg-amber-50 text-amber-700">
        <FontAwesomeIcon icon="hand-holding-dollar" />
      </span>
      <div className="flex flex-col">
        <span className="text-[13px] font-semibold text-slate-900">
          {I18n.t("projects.down_payments.banner.empty_title")}
        </span>
        <span className="text-[11.5px] text-slate-500">
          {I18n.t("projects.down_payments.banner.empty_hint", { pct: downPayment.default_pct })}
        </span>
      </div>
    </div>
    {canCreate && (
      <div className="ml-auto">
        <ActionButton onClick={onOpen}>
          {I18n.t("projects.down_payments.banner.cta")}
        </ActionButton>
      </div>
    )}
  </div>
)

const DownPaymentBanner = ({ downPayment, policies }) => {
  const [modalOpen, setModalOpen] = useState(false)
  if (!downPayment) return null
  const projectId = downPayment.create_url?.match(/\/projects\/(\d+)\//)?.[1]
  const canCreate = !!policies?.can_create_down_payment

  return (
    <div className="px-7 mt-4">
      {downPayment.exists
        ? <ExistingBlock downPayment={downPayment} />
        : <EmptyBlock downPayment={downPayment} canCreate={canCreate} onOpen={() => setModalOpen(true)} />}
      <DownPaymentModal
        isOpen      = {modalOpen}
        projectId   = {projectId}
        downPayment = {downPayment}
        onClose     = {() => setModalOpen(false)}
      />
    </div>
  )
}

export default DownPaymentBanner
